import React, { useState } from 'react';
import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import NewMessagePanel from 'components/NewMessagePanel';
import VideoVisitUnSubmittedDrawer from 'components/VideoVisitUnSubmittedDrawer';
import TextVisitSubmittedDrawer from 'components/TextVisitSubmittedDrawer';
import TextVisitUnSubmittedDrawer from 'components/TextVisitUnSubmittedDrawer';
import { getCaseDetail } from 'api/textVisit';
import type { FixActionVo, PatientRefVo, VisitRefVo } from 'types/insuranceBilling';
import s from './s.module.less';

// 抽屉里的修复按钮（visit 的 fixes[].action 与 claim 的 denialAction 共用）。
// 按钮由「action.kind + 有效目标」共同决定：目标是服务端校验过归属的引用，缺失时
// 不显示按钮（调用方照常展示建议文字），也绝不从患者姓名或 sourceVisitId 自己拼链接。
export type FixActionItem = FixActionVo & {
    // message_patient 的预填正文（只预填，provider 点 Send 才发送）
    draft?: string | null;
};

type Props = {
    actions: FixActionItem[];
    patient: string;
    patientRef: PatientRefVo | null;
    visitRef: VisitRefVo | null;
};

const isHttpUrl = (url?: string | null): url is string => !!url && /^https?:\/\//i.test(url);

export const hasActionTarget = (
    action: FixActionVo,
    patientRef: PatientRefVo | null,
    visitRef: VisitRefVo | null,
): boolean => {
    switch (action.kind) {
        // 预约走 VideoVisitDrawer（appointmentId）；text visit 走 uuid（服务端只在指派给当前医生时才给）
        case 'open_visit_note':
            return visitRef?.appointmentId != null || !!visitRef?.textVisitUuid;
        case 'open_clients':
        case 'message_patient':
            return patientRef?.prospectId != null;
        case 'open_ehr':
            return isHttpUrl(action.url);
        default:
            return false;
    }
};

const FixActions = ({ actions, patient, patientRef, visitRef }: Props) => {
    const navigate = useNavigate();
    // 打开哪种病历：预约 / 未提交的 text visit（继续写）/ 已提交的 text visit（查看）
    const [note, setNote] = useState<'appointment' | 'textAccept' | 'textView' | null>(null);
    const [noteLoading, setNoteLoading] = useState(false);
    const [msgDraft, setMsgDraft] = useState<string | null>(null);

    const usable = actions.filter((a) => hasActionTarget(a, patientRef, visitRef));
    if (!usable.length) return null;

    const prospectId = patientRef?.prospectId ?? undefined;
    const appointmentId = visitRef?.appointmentId ?? undefined;
    const textVisitUuid = visitRef?.textVisitUuid ?? undefined;

    // text visit 要先知道 note 提交了没有才能选抽屉（与 Home To-do list 同一规则）：
    // 接口没给这个状态，点击时按 uuid 取一次 case 详情
    const openNote = async () => {
        if (appointmentId != null) {
            setNote('appointment');
            return;
        }
        if (!textVisitUuid) return;
        setNoteLoading(true);
        const res: any = await getCaseDetail(textVisitUuid);
        setNoteLoading(false);
        if (res?.error) return;
        setNote(res?.data?.data?.providerTreatmentSubmitted ? 'textView' : 'textAccept');
    };

    return (
        <>
            {usable.map((a, i) => {
                const type = i === 0 ? 'primary' : 'default';
                const key = `${a.kind}-${i}`;
                const onClick = () => {
                    // 外链不用 Button href：那会渲染成 <a>，被 reset.css 的全局 .ant-btn-primary（绿色）
                    // 压过 antd 主题色，行高也会偏。统一渲染成 <button>，点击时新开标签页
                    if (a.kind === 'open_ehr') window.open(a.url!, '_blank', 'noopener,noreferrer');
                    if (a.kind === 'open_visit_note') openNote();
                    if (a.kind === 'open_clients') navigate(`${PATH.DASHBOARD}/${PATH.PROSPECTS_DETAIL}/${prospectId}`);
                    if (a.kind === 'message_patient') setMsgDraft(a.draft || '');
                };
                return (
                    <Button
                        key={key}
                        type={type}
                        className={i === 0 ? s.blueBtn : undefined}
                        loading={a.kind === 'open_visit_note' && noteLoading}
                        onClick={onClick}
                    >
                        {a.label}
                    </Button>
                );
            })}
            {note === 'appointment' && (
                <VideoVisitUnSubmittedDrawer show onClose={() => setNote(null)} appointmentId={appointmentId} />
            )}
            {note === 'textAccept' && <TextVisitUnSubmittedDrawer show onClose={() => setNote(null)} textVisitUuid={textVisitUuid} />}
            {note === 'textView' && (
                <TextVisitSubmittedDrawer open onClose={() => setNote(null)} textVisitUuid={textVisitUuid} />
            )}
            {prospectId != null && (
                <NewMessagePanel
                    open={msgDraft != null}
                    onClose={() => setMsgDraft(null)}
                    initialSendTo="patient"
                    prefillMessage={msgDraft || undefined}
                    fixedRecipient={{ label: patient, prospectId }}
                />
            )}
        </>
    );
};

export default FixActions;
